/*!
   Copyright 2019 Ron Buckton

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

       http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.
*/
import { R } from "retemplate";

/* @internal */ export const SIGN = R`[+-]`;
/* @internal */ export const DECIMAL = R`${SIGN}?\d+(?:\.\d+)?`;

// 4. Date and Time Representations

// 4.1.2 Calendar Date
/* @internal */ export const YEAR = R`(?<year>\d{4})`;
/* @internal */ export const MONTH = R`(?<month>\d{2})`;
/* @internal */ export const DAY = R`(?<day>\d{2})`;

// 4.1.3 Week Date
/* @internal */ export const WEEK_OF_YEAR = R`W(?<weekOfYear>\d{2})`;
/* @internal */ export const DAY_OF_WEEK = R`(?<dayOfWeek>\d)`;

// 4.1.4 Ordinal Date
/* @internal */ export const DAY_OF_YEAR = R`(?<dayOfYear>\d{3})`;

// 4.1.2.2 Complete Representations (Calendar Date)
/* @internal */ export const CALENDAR_DATE_EXTENDED = R`${YEAR}-${MONTH}-${DAY}`;

// 4.1.2.2 Complete Representations (Calendar Date)
// 4.1.3.2 Complete Representations (Week Date)
// 4.1.4.2 Complete Representations (Ordinal Date)
/* @internal */ export const DATE_EXTENDED = R`${YEAR}-(?:${MONTH}-${DAY}|${WEEK_OF_YEAR}-${DAY_OF_WEEK}|${DAY_OF_YEAR})`;
/* @internal */ export const DATE_BASIC = R`${YEAR}(?:${MONTH}${DAY}|${WEEK_OF_YEAR}${DAY_OF_WEEK}|${DAY_OF_YEAR})`;

// 4.1.2.3 Representations with reduced precision (Calendar Date)
/* @internal */ export const PARTIAL_CALENDAR_DATE_EXTENDED = R`(?:(?:${YEAR}-)?${MONTH}-)?${DAY}`;
/* @internal */ export const PARTIAL_CALENDAR_DATE_BASIC = R`(?:${YEAR}?${MONTH})?${DAY}`;

// 4.1.2.3 Representations with reduced precision (Week Date)
/* @internal */ export const PARTIAL_WEEK_DATE_EXTENDED = R`(?:(?:${YEAR}-)?${WEEK_OF_YEAR}-)?${DAY_OF_WEEK}`;
/* @internal */ export const PARTIAL_WEEK_DATE_BASIC = R`(?:${YEAR}?${WEEK_OF_YEAR})?${DAY_OF_WEEK}`;

// 4.1.2.3 Representations with reduced precision (Ordinal Date)
/* @internal */ export const PARTIAL_ORDINAL_DATE_EXTENDED = R`(?:${YEAR}-)?${DAY_OF_YEAR}`;
/* @internal */ export const PARTIAL_ORDINAL_DATE_BASIC = R`${YEAR}?${DAY_OF_YEAR}`;

// 4.2 Time of Day
/* @internal */ export const HOUR = R`(?<hour>\d{2})`;
/* @internal */ export const MINUTE = R`(?<minute>\d{2})`;
/* @internal */ export const SECOND = R`(?<second>\d{2})(?:\.(?<millisecond>\d{1,3})(?<nanosecond>\d{1,6})?)?`;

// 4.2.2.2 Complete Representations (Local Time)
/* @internal */ export const TIME_EXTENDED = R`${HOUR}:${MINUTE}:${SECOND}`;
/* @internal */ export const TIME_BASIC = R`${HOUR}${MINUTE}${SECOND}`;

// 4.2.2.3 Representations with reduced precision (Local Time)
/* @internal */ export const PARTIAL_TIME_EXTENDED = R`${HOUR}(?::${MINUTE}(?::${SECOND})?)?`;
/* @internal */ export const PARTIAL_TIME_BASIC = R`${HOUR}(?:${MINUTE}${SECOND}?)?`;

// 4.2.5.1 Difference between local time and UTC of day
/* @internal */ export const OFFSET_EXTENDED = R`(?<offset>Z|${SIGN}\d{2}(?::\d{2}(?::\d{2})?)?)`;
/* @internal */ export const OFFSET_BASIC = R`(?<offset>Z|${SIGN}\d{2}(?:\d{2}(?:\d{2})?)?)`;

// 4.2.4 UTC of day
// 4.2.5.2 Local time and the difference from UTC
/* @internal */ export const OFFSET_TIME_EXTENDED = R`${TIME_EXTENDED}${OFFSET_EXTENDED}`;
/* @internal */ export const OFFSET_TIME_BASIC = R`${TIME_BASIC}${OFFSET_BASIC}`;
/* @internal */ export const PARTIAL_OFFSETTIME_EXTENDED = R`${PARTIAL_TIME_EXTENDED}${OFFSET_EXTENDED}?`;
/* @internal */ export const PARTIAL_OFFSETTIME_BASIC = R`${PARTIAL_TIME_BASIC}${OFFSET_BASIC}?`;

// 4.3.3 Representations other than complete (Date and time of day)
/* @internal */ export const CALENDAR_DATETIME_EXTENDED = R`${CALENDAR_DATE_EXTENDED}T${TIME_EXTENDED}`;
/* @internal */ export const DATETIME_EXTENDED = R`${DATE_EXTENDED}T${TIME_EXTENDED}`;
/* @internal */ export const DATETIME_BASIC = R`${DATE_BASIC}T${TIME_BASIC}`;

// 4.3.2 Complete Representations (Date and time of day)
/* @internal */ export const OFFSET_DATETIME_EXTENDED = R`${DATETIME_EXTENDED}${OFFSET_EXTENDED}`;
/* @internal */ export const OFFSET_DATETIME_BASIC = R`${DATETIME_BASIC}${OFFSET_BASIC}`;

// 4.4.3 Duration
/* @internal */ export const YEARS = R`(?<years>${DECIMAL})Y`;
/* @internal */ export const MONTHS = R`(?<months>${DECIMAL})M`;
/* @internal */ export const DAYS = R`(?<days>${DECIMAL})D`;
/* @internal */ export const HOURS = R`(?<hours>${DECIMAL})H`;
/* @internal */ export const MINUTES = R`(?<minutes>${DECIMAL})M`;
/* @internal */ export const SECONDS = R`(?<seconds>${DECIMAL})S`;
/* @internal */ export const DURATION = R`(?<sign>${SIGN})?P(?!$)${YEARS}?${MONTHS}?${DAYS}?(?:T(?!$)${HOURS}?${MINUTES}?${SECONDS}?)?`;

// Non-standard
/* @internal */ export const ZONE = R`(?:\[(?<ianaZone>[\w_]+(?:\/[\w_]+)*)\])`;
/* @internal */ export const ZONED_DATETIME_EXTENDED = R`${OFFSET_DATETIME_EXTENDED}${ZONE}?`;
/* @internal */ export const ZONED_DATETIME_BASIC = R`${OFFSET_DATETIME_BASIC}${ZONE}?`;
/* @internal */ export const PARTIAL_ZONED_CALENDAR_DATETIME_EXTENDED = R`${PARTIAL_CALENDAR_DATE_EXTENDED}?(?:T${PARTIAL_OFFSETTIME_EXTENDED}${ZONE}?)?`;
/* @internal */ export const PARTIAL_ZONED_CALENDAR_DATETIME_BASIC = R`${PARTIAL_CALENDAR_DATE_BASIC}?(?:T${PARTIAL_OFFSETTIME_BASIC}${ZONE}?)?`;
/* @internal */ export const PARTIAL_ZONED_WEEK_DATETIME_EXTENDED = R`${PARTIAL_WEEK_DATE_EXTENDED}?(?:T${PARTIAL_OFFSETTIME_EXTENDED}${ZONE}?)?`;
/* @internal */ export const PARTIAL_ZONED_WEEK_DATETIME_BASIC = R`${PARTIAL_WEEK_DATE_BASIC}?(?:T${PARTIAL_OFFSETTIME_BASIC}${ZONE}?)?`;
/* @internal */ export const PARTIAL_ZONED_ORDINAL_DATETIME_EXTENDED = R`${PARTIAL_ORDINAL_DATE_EXTENDED}?(?:T${PARTIAL_OFFSETTIME_EXTENDED}${ZONE}?)?`;
/* @internal */ export const PARTIAL_ZONED_ORDINAL_DATETIME_BASIC = R`${PARTIAL_ORDINAL_DATE_BASIC}?(?:T${PARTIAL_OFFSETTIME_BASIC}${ZONE}?)?`;
